import { useCallback, useMemo } from 'react'
import { ControllerPlayer, Feature, StateChanged } from 'player'
import { useLatest } from 'react-use'
import createPersistedState from 'use-persisted-state'
import { decomposePitch } from 'player/src/hooks/use-song-key/utils'
import { Task, TaskOperations } from '../../../../types'
import { useLyrics } from '../../../../hooks/player/use-lyrics'
import { useSections } from '../../../../hooks/player/use-sections'
import { getSegmentationParamsSingleTrack } from '../../../../lib/operations/creation-params'
import { UsePersistedSettings } from '../../../../hooks/player/use-persisted-channel-settings'
import { findOperation } from '../../../../lib/operations'
import { UseModalPaywall } from '../../../../hooks/event/use-modal-paywall'

interface UseControllerProps {
  task: Task
  mainOperations: TaskOperations[]
  modalPaywall: UseModalPaywall
  persistedSettings: UsePersistedSettings
}

export const useController = ({
  task,
  mainOperations,
  modalPaywall,
  persistedSettings
}: UseControllerProps): ControllerPlayer => {
  const { id: trackId, metadata } = task
  const { onOpen: openPremiumModal } = modalPaywall
  const { update: updatePersistedSettings } = persistedSettings
  const beatschords = findOperation(mainOperations, 'beatschords')
  const originalTuning = Number(beatschords?.result?.estimatedTuning || 0)
  const useEstimatedTuningState = createPersistedState<number | null>(
    `tuning-${mainOperations[0]?.id}`
  )
  const [, setTuning] = useEstimatedTuningState(originalTuning)
  // eslint-disable-next-line max-len
  // Reason: https://github.com/donavon/use-persisted-state/issues/61#issuecomment-1629149105
  const setTuningRef = useLatest(setTuning)

  const { onAddLyrics, onGoEditorLyrics } = useLyrics({
    trackId,
    metadata,
    task
  })

  const segmentationParams = useMemo(
    () => getSegmentationParamsSingleTrack(mainOperations),
    [mainOperations]
  )
  const { onAddSections, onChangeSection } = useSections({
    metadata,
    trackId,
    segmentationParams
  })

  const onStateChanged = useCallback(
    ({ channels, panels, ...general }: Required<StateChanged>): void => {
      const { playbackRate, pitchShiftCents } = channels[0] ?? {}

      updatePersistedSettings({
        channels,
        general: { ...general, playbackRate, pitchShiftCents },
        panels
      })

      const { tuning } = decomposePitch(pitchShiftCents || 0, originalTuning)
      setTuningRef.current(tuning)
    },
    [originalTuning, setTuningRef, updatePersistedSettings]
  )

  const onLockedFeatureClick = useCallback(
    (feature: Feature, automaticDisplayed = false) => {
      openPremiumModal(feature as any, automaticDisplayed)
    },
    [openPremiumModal]
  )

  return useMemo(
    () => ({
      lyrics: { onAddLyrics, onGoEditorLyrics },
      sections: { onAddSections, onChangeSection },
      onStateChanged,
      onLockedFeatureClick
    }),
    [
      onAddLyrics,
      onAddSections,
      onStateChanged,
      onChangeSection,
      onGoEditorLyrics,
      onLockedFeatureClick
    ]
  )
}
